import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Sparkles, User, Building2, Plus, Edit, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAllUpdates, createUpdate, updateUpdate, deleteUpdate } from '../services/firebase/updates';
import Modal from '../components/Modal';
import PortalBackground from '../components/PortalBackground';

const InternalWhatsNew = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newUpdate, setNewUpdate] = useState({
    title: '',
    description: '',
    department: 'General',
    type: 'Available Now',
    quarter: currentQuarter.toString(),
    year: currentYear.toString()
  });

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const updatesData = await getAllUpdates();
      setUpdates(updatesData);
    } catch (err) {
      console.error('Error fetching updates:', err);
      setError('Failed to load updates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await createUpdate(newUpdate);
      setShowAddModal(false);
      setNewUpdate({
        title: '',
        description: '',
        department: 'General',
        type: 'Available Now',
        quarter: currentQuarter.toString(),
        year: currentYear.toString()
      });
      await fetchUpdates(); // Refresh the list
    } catch (err) {
      console.error('Error creating update:', err);
      alert('Failed to create update. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUpdate = (update) => {
    setSelectedUpdate(update);
    setNewUpdate({
      title: update.title || '',
      description: update.description || '',
      department: update.department || 'General',
      type: update.type || 'Available Now',
      quarter: update.quarter || currentQuarter.toString(),
      year: update.year || currentYear.toString()
    });
    setShowEditModal(true);
  };

  const handleUpdateUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUpdate) return;
    
    setIsSubmitting(true);
    
    try {
      await updateUpdate(selectedUpdate.id, newUpdate);
      setShowEditModal(false);
      setSelectedUpdate(null);
      setNewUpdate({
        title: '',
        description: '',
        department: 'General',
        type: 'Available Now',
        quarter: currentQuarter.toString(),
        year: currentYear.toString()
      });
      await fetchUpdates(); // Refresh the list
    } catch (err) {
      console.error('Error updating update:', err);
      alert('Failed to update update. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (update) => {
    setSelectedUpdate(update);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUpdate) return;
    
    setIsDeleting(true);
    
    try {
      await deleteUpdate(selectedUpdate.id);
      setShowDeleteConfirm(false);
      setSelectedUpdate(null);
      await fetchUpdates(); // Refresh the list
    } catch (err) {
      console.error('Error deleting update:', err);
      alert('Failed to delete update. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const isOwner = (update) => {
    return currentUser && update.createdByUid === currentUser.uid;
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDepartmentGradient = (department) => {
    const gradients = {
      'Sales': 'from-red-500 to-red-600',
      'Operations': 'from-blue-500 to-blue-600',
      'Technology': 'from-purple-500 to-purple-600',
      'Finance': 'from-green-500 to-green-600',
      'HR': 'from-yellow-500 to-yellow-600',
      'Marketing': 'from-orange-500 to-orange-600',
      'General': 'from-gray-500 to-gray-600'
    };
    return gradients[department] || gradients['General'];
  };

  const departments = ['General', 'Sales', 'Marketing', 'Operations', 'Finance', 'Technology', 'HR'];

  return (
    <div className="min-h-screen relative transition-colors duration-200">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 relative">
          <button
            onClick={() => setShowAddModal(true)}
            className={`absolute right-0 top-0 w-12 h-12 rounded-full flex items-center justify-center ${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} transition-colors shadow-lg hover:shadow-xl`}
          >
            <Plus className="w-6 h-6" />
          </button>
          <div className="flex justify-center">
            <h1 className={`text-4xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              What's New
            </h1>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <BBALoader size={48} />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {error}
          </div>
        )}

        {/* Updates Timeline */}
        {!loading && !error && (
          <div className="relative">
            {/* Timeline Line */}
            <div className={`absolute left-8 top-0 bottom-0 w-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>

            {/* Updates List */}
            <div className="space-y-8">
              {updates.length === 0 ? (
                <div className={`text-center py-20 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-xl">No updates available yet</p>
                  <p className="text-sm mt-2">Click the + button to add your first update!</p>
                </div>
              ) : (
                updates.map((update, index) => (
                  <div key={update.id} className="relative flex gap-6">
                    {/* Timeline Dot */}
                    <div className={`relative z-10 flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-md`}>
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getDepartmentGradient(update.department || 'General')}`}></div>
                    </div>

                    {/* Update Card */}
                    <div className={`flex-1 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${isOwner(update) ? 'cursor-pointer' : ''}`}
                      onClick={isOwner(update) ? () => handleEditUpdate(update) : undefined}>
                      {/* Card Header with Gradient */}
                      <div className={`bg-gradient-to-r ${getDepartmentGradient(update.department || 'General')} p-6`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white bg-white/20 backdrop-blur-sm`}>
                                {update.department || 'General'}
                              </span>
                              <span className={`text-sm ${darkMode ? 'text-white/80' : 'text-white/90'}`}>
                                {formatDate(update.createdAt)}
                              </span>
                            </div>
                            <h2 className={`text-2xl font-bold text-white mb-2`}>
                              {update.title || 'Untitled Update'}
                            </h2>
                          </div>
                          {isOwner(update) && (
                            <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditUpdate(update);
                                }}
                                className={`p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors`}
                                title="Edit"
                              >
                                <Edit className="w-4 h-4 text-white" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(update);
                                }}
                                className={`p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors`}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-6">
                        <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed mb-6 whitespace-pre-wrap`}>
                          {update.description || 'No description provided.'}
                        </p>

                        {/* Card Footer */}
                        <div className={`flex items-center gap-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <User className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {update.createdBy || 'Unknown Author'}
                            </span>
                          </div>
                          {update.department && (
                            <div className="flex items-center gap-2">
                              <Building2 className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {update.department}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Update Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
        <div className="w-full max-w-2xl">
          <div className="flex justify-center items-center mb-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Add New Update
            </h2>
          </div>

          <form onSubmit={handleAddUpdate} className="space-y-6">
            {/* Title */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Title *
              </label>
              <input
                type="text"
                value={newUpdate.title}
                onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                placeholder="Enter update title"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description *
              </label>
              <textarea
                value={newUpdate.description}
                onChange={(e) => setNewUpdate({ ...newUpdate, description: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                rows="6"
                placeholder="Enter update description"
                required
              />
            </div>

            {/* Department */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Department *
              </label>
              <select
                value={newUpdate.department}
                onChange={(e) => setNewUpdate({ ...newUpdate, department: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                required
              >
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Type *
              </label>
              <select
                value={newUpdate.type}
                onChange={(e) => setNewUpdate({ ...newUpdate, type: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                required
              >
                <option value="Available Now">Available Now</option>
                <option value="Future">Future</option>
              </select>
            </div>

            {/* Quarter and Year - Only show when Type is "Future" */}
            {newUpdate.type === 'Future' && (
              <div className="grid grid-cols-2 gap-4">
                {/* Quarter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Quarter *
                  </label>
                  <select
                    value={newUpdate.quarter}
                    onChange={(e) => setNewUpdate({ ...newUpdate, quarter: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                    required
                  >
                    <option value="1">Q1</option>
                    <option value="2">Q2</option>
                    <option value="3">Q3</option>
                    <option value="4">Q4</option>
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Year *
                  </label>
                  <select
                    value={newUpdate.year}
                    onChange={(e) => setNewUpdate({ ...newUpdate, year: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                    required
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = currentYear + i;
                      return (
                        <option key={year} value={year.toString()}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className={`px-6 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? 'Creating...' : 'Create Update'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Update Modal */}
      <Modal isOpen={showEditModal} onClose={() => {
        setShowEditModal(false);
        setSelectedUpdate(null);
      }}>
        <div className="w-full max-w-2xl">
          <div className="flex justify-center items-center mb-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Edit Update
            </h2>
          </div>

          <form onSubmit={handleUpdateUpdate} className="space-y-6">
            {/* Title */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Title *
              </label>
              <input
                type="text"
                value={newUpdate.title}
                onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                placeholder="Enter update title"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description *
              </label>
              <textarea
                value={newUpdate.description}
                onChange={(e) => setNewUpdate({ ...newUpdate, description: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                rows="6"
                placeholder="Enter update description"
                required
              />
            </div>

            {/* Department */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Department *
              </label>
              <select
                value={newUpdate.department}
                onChange={(e) => setNewUpdate({ ...newUpdate, department: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                required
              >
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Type *
              </label>
              <select
                value={newUpdate.type}
                onChange={(e) => setNewUpdate({ ...newUpdate, type: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                required
              >
                <option value="Available Now">Available Now</option>
                <option value="Future">Future</option>
              </select>
            </div>

            {/* Quarter and Year - Only show when Type is "Future" */}
            {newUpdate.type === 'Future' && (
              <div className="grid grid-cols-2 gap-4">
                {/* Quarter */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Quarter *
                  </label>
                  <select
                    value={newUpdate.quarter}
                    onChange={(e) => setNewUpdate({ ...newUpdate, quarter: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                    required
                  >
                    <option value="1">Q1</option>
                    <option value="2">Q2</option>
                    <option value="3">Q3</option>
                    <option value="4">Q4</option>
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Year *
                  </label>
                  <select
                    value={newUpdate.year}
                    onChange={(e) => setNewUpdate({ ...newUpdate, year: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                    required
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = currentYear + i;
                      return (
                        <option key={year} value={year.toString()}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUpdate(null);
                }}
                className={`px-6 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={() => {
        setShowDeleteConfirm(false);
        setSelectedUpdate(null);
      }}>
        <div className="w-full max-w-md">
          <div className="flex justify-center items-center mb-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Delete Update
            </h2>
          </div>

          <div className="mb-6">
            <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Are you sure you want to delete this update? This action cannot be undone.
            </p>
            {selectedUpdate && (
              <p className={`text-center mt-2 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                "{selectedUpdate.title}"
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setSelectedUpdate(null);
              }}
              className={`px-6 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className={`px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InternalWhatsNew;

